// The OneVideo and InFront sections' panels (pipeline RC4; brief of 7 Oct sections 4a and 4b, drawings a6-account-file.html
// "OneVideo" and c5-states.html "InFront's streams after 1 July"). OneVideo is read per agreement, the share of the events
// booked for the client that it took against its own usual, never the whole basket; InFront's streams are drawn as the
// three-box change of 1 July with the reading of the months since. Every figure is track B's reading (RC2) as the usage
// route returns it. The view across all of InFront's clients belongs on InFront's own record, so nothing here names another.
import { ActionButton } from '../parts/index.js';
import { useState } from 'react';
import { Spark } from './OneTradeCharts';
import { agreementRows, inFrontWords, leagueRows, monthName, monthShort, type InFrontReading, type OneVideoAgreementReading, type OneVideoClientLeagues } from './readings';

const FIRST = 10;

export function OneVideoAgreements({ agreements, month = null }: { agreements: readonly OneVideoAgreementReading[]; month?: string | null }) {
  const [all, setAll] = useState(false);
  const rows = agreementRows(agreements, month);
  if (rows.length === 0) return null;
  const shown = all ? rows : rows.slice(0, FIRST);
  const name = monthName(rows[0]!.last);
  return (
    <div className="ot-top">
      <p className="colh">Use by agreement, against the events booked for it</p>
      <table className="ot-leagues ov-agreements">
        <thead><tr><th>Agreement</th><th className="n">{name}, taken of booked</th><th className="n">Usual</th><th>Reading</th><th className="n">Twelve months</th><th>By month</th></tr></thead>
        <tbody>
          {shown.map((r) => (
            <tr key={r.key}>
              <th scope="row">{r.competition}<span className="ot-sport">{[r.rightsholder, r.sport].filter(Boolean).join(', ')}</span></th>
              <td className="n" data-l={`${name}, taken of booked`}>{r.lastWords}</td>
              <td className="n" data-l="Usual">{r.usualWords ?? (r.reading ? 'none yet' : '')}</td>
              <td data-l="Reading">{r.reading ?? 'not booked'}</td>
              <td className="n" data-l="Twelve months">{r.totalWords ?? 'none booked'}</td>
              <td data-l="By month"><Spark values={r.spark} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > FIRST && !all ? <ActionButton reading type="button" className="hx-link" onClick={() => setAll(true)}>Show all {rows.length} agreements</ActionButton> : null}
    </div>
  );
}

/** OneVideo's top ten leagues over twelve months, all leagues together, InFront's marked ended (RC15; U372). */
export function OneVideoLeagues({ lists }: { lists: readonly OneVideoClientLeagues[] }) {
  const shown = lists.filter((c) => c.leagues.length > 0);
  if (shown.length === 0) return null;
  return <>{shown.map((c, i) => <LeagueList key={c.operatorId} c={c} of={shown.length > 1 ? `${i + 1} of ${shown.length} feeds` : null} />)}</>;
}

function LeagueList({ c, of }: { c: OneVideoClientLeagues; of: string | null }) {
  const rows = leagueRows(c);
  return (
    <div className="ot-top">
      <p className="colh">The leagues it took most, events in the twelve months to {monthShort(c.through)}{of ? `, ${of}` : ''}</p>
      <table className="ot-leagues ov-leagues">
        <thead><tr><th>League</th><th className="n">Events, 12 months</th><th className="n">Since 1 July</th><th>Against the year before</th><th className="n">Views, 12 months</th><th>By month</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={r.ended ? 'ov-ended' : undefined}>
              <th scope="row">{r.league}<span className="ot-sport">{[r.sport, r.holders].filter(Boolean).join(', ')}</span>{r.ended ? <span className="ot-sport ov-ended-note">{r.ended}</span> : null}</th>
              <td className="n" data-l="Events, 12 months">{r.events}{r.share ? <span className="ot-sport">{r.share}</span> : null}</td>
              <td className="n" data-l="Since 1 July">{r.sinceJuly}</td>
              <td data-l="Against the year before">{r.against}</td>
              <td className="n" data-l="Views, 12 months">{r.views}</td>
              <td data-l="By month"><Spark values={r.spark} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function InFrontPanels({ readings, name, wentDirect = null }: { readings: readonly InFrontReading[]; name: string; wentDirect?: string | null }) {
  if (readings.length === 0) return null;
  return <>{readings.map((r, i) => <InFrontPanel key={r.operatorId} r={r} name={name} wentDirect={wentDirect} of={readings.length > 1 ? `${i + 1} of ${readings.length} feeds` : null} />)}</>;
}

/** `wentDirect`: RC9's note in words, where one stands; the third box then says so in place of the reading (c5-states.html). */
function InFrontPanel({ r, name, of, wentDirect }: { r: InFrontReading; name: string; of: string | null; wentDirect: string | null }) {
  const w = wentDirect ? { ...inFrontWords(r, name), since: 'Went direct to InFront', sinceNote: `${wentDirect} InFront's streams are no longer measured for ${name}.` } : inFrontWords(r, name);
  return (
    <div className="if-change">
      <p className="colh">InFront's streams, the change in July{of ? `, ${of}` : ''}</p>
      <ol className="if-boxes">
        <li><span className="if-when">To 30 June 2026</span><b>Served by iGame</b><span>{w.before}</span></li>
        <li><span className="if-when">1 July 2026</span><b>InFront's contract with iGame ended</b><span>iGame no longer serves InFront's video; clients on our integration still call for it, and we see those calls.</span></li>
        <li className={r.label === 'on_integration' || wentDirect ? undefined : 'if-now'}><span className="if-when">{wentDirect ? 'Since 1 July 2026' : `Since, to ${monthName(r.asOf)}`}</span><b>{w.since}</b><span>{w.sinceNote}</span></li>
      </ol>
    </div>
  );
}
